import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import { useDispatch, useSelector } from "react-redux";
import { usePdfDownloader } from "../common/usePdfDownloader";
import { GLOBALTYPES } from "../redux/actions/globalTypes";
import { getDataAPI, postDataAPI } from "../utils/fetchData";
import Adminnavbar from "./adminnavbar";
import Footer from "./footer";
import LoginRequest from "./loginRequest";
import Sidenavbar from "./sidenav";

// Constants
const CONTAINER_STYLE = { backgroundColor: "#ffffff", padding: "30px" };
const TABLE_HEADER_STYLE = {
	backgroundColor: "rgb(167, 213, 136)",
	fontSize: "15px",
	fontWeight: "700",
	letterSpacing: "0.5px",
	textAlign: "center",
};
const CELL_STYLE = {
	fontSize: "20px",
	textAlign: "center",
	fontFamily: "Roboto, sans-serif",
};
const VIEW_BUTTON_STYLE = {
	backgroundColor: "#00ccff",
	border: "none",
	borderRadius: "4px",
	margin: "5px",
	color: "#fff",
};
const DELETE_BUTTON_STYLE = {
	background: "#E23F1C",
	fontSize: "13px",
	borderRadius: "4px",
	border: "none",
};
const ERROR_STYLE = {
	visibility: "visible",
};

const Superadmin = () => {
	const { auth } = useSelector((state) => state);
	const dispatch = useDispatch();
	const downloadPdf = usePdfDownloader();

	// Modal state
	const [show, setShow] = useState(false);
	const [editShow, setEditShow] = useState(false);

	// Form state
	const [name, setName] = useState("");
	const [order, setOrder] = useState("");
	const [document, setDocument] = useState("");
	const [loading, setLoading] = useState(false);

	// Edit state
	const [editId, setEditId] = useState(null);
	const [editName, setEditName] = useState("");
	const [editOrder, setEditOrder] = useState("");
	const [editDocument, setEditDocument] = useState(null);
	const [editLoading, setEditLoading] = useState(false);

	// Error state
	const [nameError, setNameError] = useState("");
	const [orderError, setOrderError] = useState("");
	const [documentError, setDocumentError] = useState("");
	const [editNameError, setEditNameError] = useState("");
	const [editOrderError, setEditOrderError] = useState("");
	const [editDocumentError, setEditDocumentError] = useState("");

	// Data state
	const [complience, setComplience] = useState([]);

	// Modal handlers
	const handleClose = useCallback(() => {
		setShow(false);
		setName("");
		setOrder("");
		setDocument("");
		setNameError("");
		setOrderError("");
		setDocumentError("");
	}, []);

	const handleShow = useCallback(() => setShow(true), []);

	const handleEditClose = useCallback(() => {
		setEditShow(false);
		setEditId(null);
		setEditName("");
		setEditOrder("");
		setEditDocument(null);
		setEditNameError("");
		setEditOrderError("");
		setEditDocumentError("");
	}, []);

	const handleEditShow = useCallback((row) => {
		setEditShow(true);
		setEditId(row.id);
		setEditName(row.name);
		setEditOrder(row.order);
		setEditDocument(null);
		setEditNameError("");
		setEditOrderError("");
		setEditDocumentError("");
	}, []);

	// Form validation
	const validateForm = useCallback(() => {
		let isValid = true;

		// Clear previous errors
		setNameError("");
		setOrderError("");
		setDocumentError("");

		// Validation checks
		if (!name) {
			setNameError("Name is required");
			isValid = false;
		}

		if (!order || isNaN(order)) {
			setOrderError("Order must be a valid number");
			isValid = false;
		}

		if (!document) {
			setDocumentError("Document is required");
			isValid = false;
		} else if (document && document.type !== "application/pdf") {
			setDocumentError("Only PDF files are allowed");
			isValid = false;
		}

		return isValid;
	}, [name, order, document]);

	const validateEditForm = useCallback(() => {
		let isValid = true;
		setEditNameError("");
		setEditOrderError("");
		setEditDocumentError("");

		if (!editName) {
			setEditNameError("Name is required");
			isValid = false;
		}
		if (!editOrder || isNaN(editOrder)) {
			setEditOrderError("Order must be a valid number");
			isValid = false;
		}
		// Document is optional for edit

		return isValid;
	}, [editName, editOrder]);

	// Form submission handler
	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();

			if (!validateForm()) {
				return;
			}

			setLoading(true);
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const sendData = new FormData();
				if (document !== "") {
					sendData.append("files", document);
				}
				sendData.append("name", name);
				sendData.append("order", order);

				const res = await postDataAPI("createregulatory", sendData, auth.token);

				if (res.status === 200) {
					alert("Upload Successful!!!");
					window.location.reload();
				}
			} catch (err) {
				console.error("Error creating regulatory compliance:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error:
							err.response?.data?.msg || "Error creating regulatory compliance",
					},
				});
			} finally {
				setLoading(false);
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[name, order, document, validateForm, auth.token, dispatch]
	);

	// Edit submission handler
	const handleEditSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			if (!validateEditForm()) return;
			setEditLoading(true);
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
			try {
				const sendData = new FormData();
				if (editDocument) {
					sendData.append("files", editDocument);
				}
				sendData.append("name", editName);
				sendData.append("order", editOrder);
				sendData.append("id", editId);

				const res = await postDataAPI(`updateregulatory`, sendData, auth.token);

				if (res.status === 200) {
					alert("Update Successful!");
					window.location.reload();
				}
			} catch (err) {
				console.error("Error updating regulatory compliance:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error:
							err.response?.data?.msg || "Error updating regulatory compliance",
					},
				});
			} finally {
				setEditLoading(false);
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[
			editId,
			editName,
			editOrder,
			editDocument,
			validateEditForm,
			auth.token,
			dispatch,
		]
	);

	// File change handler
	const handleChange = useCallback((e) => {
		let file = e.target.files[0];
		if (file && file.type !== "application/pdf") {
			setDocumentError("Only PDF files are allowed!");
			setDocument(null);
		} else {
			setDocumentError("");
			setDocument(file);
		}
	}, []);

	const handleEditFileChange = useCallback((e) => {
		let file = e.target.files[0];
		if (file && file.type !== "application/pdf") {
			setEditDocumentError("Only PDF files are allowed!");
			setEditDocument(null);
		} else {
			setEditDocumentError("");
			setEditDocument(file);
		}
	}, []);

	// Fetch regulatory compliances
	useEffect(() => {
		const fetchRegulatoryCompliances = async () => {
			try {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
				const res = await getDataAPI(`getregulatories`);
				setComplience(res.data.regulatories || []);
			} catch (err) {
				console.error("Error fetching regulatory compliances:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch regulatory compliances" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		};

		fetchRegulatoryCompliances();
	}, [dispatch]);

	// Delete handler
	const handleDelete = useCallback(
		async (reg) => {
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const res = await getDataAPI(
					`inactiveregulatory/${reg.id}`,
					auth.token
				);

				if (res.status === 200) {
					window.location.reload();
				}
			} catch (err) {
				console.error("Error deleting regulatory compliance:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error:
							err.response?.data?.msg || "Error deleting regulatory compliance",
					},
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[auth.token, dispatch]
	);

	// Memoized table rows
	const tableRows = useMemo(() => {
		if (complience.length === 0) {
			return (
				<tr>
					<td colSpan="6" style={{ textAlign: "center" }}>
						No data available
					</td>
				</tr>
			);
		}

		return complience.map((val, index) => (
			<tr
				key={val.id || index}
				style={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
				<td style={CELL_STYLE}>{index + 1}</td>
				<td style={CELL_STYLE}>{val.name}</td>
				<td style={CELL_STYLE}>{val.order}</td>
				<td style={CELL_STYLE}>
					<Button
						onClick={() => downloadPdf(val.source)}
						style={VIEW_BUTTON_STYLE}>
						View File
					</Button>
				</td>
				<td style={CELL_STYLE}>
					<Button
						style={DELETE_BUTTON_STYLE}
						className="btn btn-danger mt-2"
						onClick={() => handleDelete(val)}>
						<i className="fa fa-trash" aria-hidden="true"></i>
					</Button>
					<Button
						style={{
							backgroundColor: "#36BB7D",
							fontSize: "13px",
							borderRadius: "4px",
							border: "none",
							marginLeft: "8px",
							color: "#fff",
						}}
						className="btn btn-success mt-2"
						onClick={() => handleEditShow(val)}>
						<i className="fa fa-edit" aria-hidden="true"></i>
					</Button>
				</td>
			</tr>
		));
	}, [complience, downloadPdf, handleDelete, handleEditShow]);

	if (!auth.user) {
		return <LoginRequest />;
	}

	return (
		<>
			<Adminnavbar />
			<div style={CONTAINER_STYLE}>
				<div className="button-format">
					<div className="dashboard-text">
						Regulatory Compliances Management
					</div>
					<button className="addinstructor-btn" onClick={handleShow}>
						+ ADD
					</button>
					<Modal show={show} onHide={handleClose}>
						<div className="js">
							<div className="x-style" onClick={handleClose}>
								<span style={{ cursor: "pointer" }}>X</span>
							</div>
							<div className="js-boxIn">
								<div className="js-boxIn-content">
									<div className="ai-greenbox">
										<div>
											<h4 className="js-greenbox-heading">
												Regulatory Compliances
											</h4>
										</div>
										<div>
											<form onSubmit={handleSubmit}>
												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="text"
															value={name}
															placeholder="Name"
															onChange={(e) => setName(e.target.value)}
														/>
													</span>
												</div>

												<p
													style={{
														visibility: nameError ? "visible" : "hidden",
													}}
													className="error-message">
													{nameError || "Name is required."}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="number"
															value={order}
															placeholder="Order"
															onChange={(e) => setOrder(e.target.value)}
														/>
													</span>
												</div>

												<p
													style={{
														visibility: orderError ? "visible" : "hidden",
													}}
													className="error-message">
													{orderError || "Order is required."}
												</p>

												<b>
													<span>Note: Please upload file in pdf format</span>
												</b>
												<div className="js-inputbox">
													<span className="scclass-inputbox-input">
														<label>Document: </label>{" "}
														<input
															type="file"
															placeholder="document"
															onChange={handleChange}
															accept="application/pdf"
														/>
													</span>
												</div>

												<p
													className="error-message"
													style={{
														visibility: documentError ? "visible" : "hidden",
													}}>
													{documentError || "Document is required."}
												</p>

												<button
													type="submit"
													className="js-submitbtn"
													disabled={loading}>
													{loading ? "SUBMITTING..." : "SUBMIT"}
												</button>
											</form>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Modal>
					<Modal show={editShow} onHide={handleEditClose}>
						<div className="js">
							<div className="x-style" onClick={handleEditClose}>
								<span style={{ cursor: "pointer" }}>X</span>
							</div>
							<div className="js-boxIn">
								<div className="js-boxIn-content">
									<div className="ai-greenbox">
										<div>
											<h4 className="js-greenbox-heading">
												Edit Regulatory Compliance
											</h4>
										</div>
										<div>
											<form onSubmit={handleEditSubmit}>
												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="text"
															value={editName}
															placeholder="Name"
															onChange={(e) => setEditName(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: editNameError ? "visible" : "hidden",
													}}
													className="error-message">
													{editNameError || "Name is required."}
												</p>
												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="number"
															value={editOrder}
															placeholder="Order"
															onChange={(e) => setEditOrder(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: editOrderError ? "visible" : "hidden",
													}}
													className="error-message">
													{editOrderError || "Order is required."}
												</p>
												<b>
													<span>Note: Please upload file in pdf format</span>
												</b>
												<div className="js-inputbox">
													<span className="scclass-inputbox-input">
														<label>Document: </label>{" "}
														<input
															type="file"
															placeholder="document"
															onChange={handleEditFileChange}
															accept="application/pdf"
														/>
													</span>
												</div>
												<p
													className="error-message"
													style={{
														visibility: editDocumentError
															? "visible"
															: "hidden",
													}}>
													{editDocumentError}
												</p>
												<button
													type="submit"
													className="js-submitbtn"
													disabled={editLoading}>
													{editLoading ? "UPDATING..." : "UPDATE"}
												</button>
											</form>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Modal>
				</div>
				<div>
					<div className="row format">
						<Sidenavbar />
						<div className="col-8">
							<Table>
								<thead id="pl">
									<tr style={TABLE_HEADER_STYLE}>
										<th>S.No</th>
										<th>Name</th>
										<th>Order</th>
										<th>Document</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>{tableRows}</tbody>
							</Table>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

const styles = {
	evenRow: {
		backgroundColor: "#f9f9f9",
	},
	oddRow: {
		backgroundColor: "#e9e9e9",
	},
};

export default Superadmin;
